/**
 * Environment Variable Validation
 * Validates required environment variables at application startup
 * Prevents runtime errors due to missing configuration
 */

/**
 * List of required environment variables
 */
const REQUIRED_ENV_VARS = ["REACT_APP_API_URL"];

/**
 * List of optional environment variables with default values
 */
const OPTIONAL_ENV_VARS = {
  // Add optional vars here if needed
  // Example: REACT_APP_FEATURE_FLAG: "false"
};

/**
 * Validate environment variables
 * Throws error if required variables are missing
 * Warns if optional variables are missing
 *
 * @throws {Error} If required environment variables are missing
 *
 * @example
 * import { validateEnv } from "./config/validateEnv";
 * validateEnv(); // Call at app startup
 */
export function validateEnv() {
  const missingVars = [];
  const warnings = [];

  // Check required variables
  REQUIRED_ENV_VARS.forEach((varName) => {
    const value = process.env[varName];

    if (!value || value.trim() === "") {
      missingVars.push(varName);
    } else {
    //   console.log(`✓ ${varName}: ${value}`);
    }
  });

  // Check optional variables
  Object.keys(OPTIONAL_ENV_VARS).forEach((varName) => {
    const value = process.env[varName];

    if (!value || value.trim() === "") {
      warnings.push({
        name: varName,
        defaultValue: OPTIONAL_ENV_VARS[varName],
      });
    }
  });

  // Report warnings for optional variables
  if (warnings.length > 0) {
    console.warn("⚠️  Optional environment variables not set:");
    warnings.forEach(({ name, defaultValue }) => {
      console.warn(`   - ${name} (using default: ${defaultValue})`);
    });
  }

  // Throw error if required variables are missing
  if (missingVars.length > 0) {
    const errorMessage = `
╔════════════════════════════════════════════════════════════════╗
║  ENVIRONMENT CONFIGURATION ERROR                               ║
╠════════════════════════════════════════════════════════════════╣
║                                                                ║
║  Missing required environment variables:                       ║
║  ${missingVars.map((v) => `- ${v}`).join("\n║  ")}
║                                                                ║
║  Please create a .env file in the project root with:          ║
║  ${missingVars.map((v) => `${v}=<value>`).join("\n║  ")}
║                                                                ║
║  Example .env file:                                            ║
║  REACT_APP_API_URL=http://localhost:8081/api                   ║
║                                                                ║
╚════════════════════════════════════════════════════════════════╝
    `.trim();

    throw new Error(errorMessage);
  }

  // Validate URL format for API_URL
  const apiUrl = process.env.REACT_APP_API_URL;
  try {
    new URL(apiUrl);
  } catch (err) {
    console.warn(`⚠️  REACT_APP_API_URL is not a valid URL: ${apiUrl}`);
  }

}

/**
 * Get environment variable with fallback
 *
 * @param {string} varName - Environment variable name
 * @param {string} fallback - Fallback value if not found
 * @returns {string} Environment variable value or fallback
 */
export function getEnv(varName, fallback = "") {
  return process.env[varName] || fallback;
}

/**
 * Check if running in development mode
 */
export function isDevelopment() {
  return process.env.NODE_ENV === "development";
}

/**
 * Check if running in production mode
 */
export function isProduction() {
  return process.env.NODE_ENV === "production";
}
